/**
 * L'ACCÈS DE LA PERSONNE COURANTE AUX GESTES DU MODULE.
 * ============================================================================
 *
 * INTENTION : chaque écran demande, pour un geste, s'il le montre actif,
 * inactif avec sa raison, ou pas du tout, sans jamais juger un droit lui-même.
 *
 * Le contexte se lit, il ne se suppose pas :
 *  • LE RÔLE est `auth.role` (`core/stores/auth.store.ts`, rôle `role`), tenu
 *    par `fetchUser()` depuis `company.userCompanyType` de l'entreprise de la
 *    session. Une session de l'équipe ouverte au mot de passe maître est celle
 *    du compte regardé (`auth.staff.ts`) : elle a donc le rôle de ce compte,
 *    jamais la colonne `support` (plan §6 point 15). Un rôle inconnu ou non lu
 *    (`null`) rend tout absent.
 *  • L'ABONNEMENT vient de `ouverture.store` (lot F1) : `hasOfferEnergyManager`
 *    et l'offre servie par `GET /offre`, seulement quand cette lecture vaut
 *    pour la session courante (personne et entreprise) et que la garde serveur
 *    a ouvert le module. Sinon rien n'est su : tout est absent.
 *  • LE SOLDE DE SMS : par défaut, celui de l'offre lue. Un écran qui relit le
 *    solde (`GET /sms`, après une recharge faite en session) passe cette
 *    lecture en option `sms` : la colonne `em_avance_solde_epuise` cède alors
 *    la place à `em_avance` dès que le solde relu n'est plus épuisé, et
 *    inversement, sans recharger la page. Un solde inconnu (`null`) ne ferme
 *    rien (plan §6 point 16). C'est à l'écran de ne passer qu'une lecture de
 *    la session courante.
 */
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { useOuvertureStore } from "@modules/alarmes-v2/stores/ouverture.store"
import {
    acces,
    abonnementCourant,
    roleDeLaSession,
    type AbonnementMatrice,
    type Acces,
    type CleGeste,
    type RoleDuFront,
} from "@modules/alarmes-v2/acces/acces"
import type { AlarmesV2Sms } from "@modules/alarmes-v2/types/alarmes-v2"

export interface OptionsUseAcces {
    /**
     * La lecture la plus récente du solde de SMS (`GET /sms`) ; `undefined`
     * reprend le solde de l'offre.
     */
    sms?: MaybeRefOrGetter<AlarmesV2Sms | null | undefined>
}

export interface UseAcces {
    /** Le rôle de la matrice lu pour la session, `null` s'il n'est pas su. */
    role: ComputedRef<RoleDuFront | null>
    /** L'abonnement de la matrice lu pour la session, `null` s'il n'est pas su. */
    abonnement: ComputedRef<AbonnementMatrice | null>
    /**
     * Le verdict d'un geste. `condition` : la condition de l'objet (être
     * destinataire de l'alarme), voir `ContexteAcces.condition`.
     */
    acces: (cle: CleGeste, condition?: boolean | null) => Acces
}

export function useAcces(options: OptionsUseAcces = {}): UseAcces {
    const auth = useAuthStore()
    const ouverture = useOuvertureStore()

    const role = computed(() => roleDeLaSession(auth.role))

    const abonnement = computed((): AbonnementMatrice | null => {
        if (!ouverture.luPourLaSession || !ouverture.ouvert) return null
        return abonnementCourant({
            hasOfferEnergyManager: auth.hasOfferEnergyManager,
            offre: ouverture.offre,
            sms: toValue(options.sms),
        })
    })

    return {
        role,
        abonnement,
        acces: (cle, condition) =>
            acces(cle, {
                role: role.value,
                abonnement: abonnement.value,
                condition,
            }),
    }
}
